'use client';

import {
  isRateLimitedPublicSearchType,
  SEARCH_TYPE,
} from '@/constants/searchType';
import { useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Stack, Typography } from '@/primitives';
import SearchHeader from '@/components/publicSearch/searchPage/SearchHeader';
import SearchResult from '@/components/publicSearch/searchPage/SearchResult';
import RateLimitBanner from '@/components/publicSearch/RateLimitBanner';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import usePublicSearchLimit from '@/components/publicSearch/heroSection/usePublicSearchLimit';
import { SearchProvider } from '@/hooks/publicSearch/useSearch';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useIsI4Society } from '@/hooks/publicSearch/useIsI4Society';
import { enqueueSnackbar } from '@/providers/SnackbarProvider';
import SaleNote from '@/components/publicSearch/sale-catalogues/SaleNote';
import SaleInfoPanel from '@/components/publicSearch/sale-catalogues/SaleInfoPanel';
import { MatingPredictorPage } from '@/components/publicSearch/mating';
import { useSaleIdParam } from '@/hooks/publicSearch/useSaleIdParam';
import { useSharedSearchHydration } from '@/hooks/publicSearch/useSharedSearchHydration';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useGetSaleDetails } from '@/hooks/publicSearch/sale-catalogue/useGetSaleDetails';
import DownloadsPage from '@/components/publicSearch/i4/DownloadsPage';
import OnlineRegistrationsTab from '@/components/publicSearch/i4/OnlineRegistrationsTab';
import { buildSocietySearchHref } from '@/lib/publicSearchPaths';
import { useIsEbvEnabled } from '@/hooks/publicSearch/useIsEbvEnabled';
import { SNACKBAR_VARIANT } from '@/constants/notifications';
import { SEARCH_PARAM } from '@/constants/searchParams';

export default function SocietySearchPage() {
  return (
    <SearchProvider>
      <SocietySearchPageInner />
    </SearchProvider>
  );
}

function SocietySearchPageInner() {
  const router = useRouter();
  const params = useParams<{ orgName?: string }>();
  const orgSegment = params.orgName ?? '';
  const { setOpenResult } = useSearchStore();
  const society = usePublicSearchContext((context) => context.society);
  const isEbvEnabled = useIsEbvEnabled();
  const orgId = society.orgId;
  const searchType = useSearchType();
  const isI4Society = useIsI4Society();
  const saleId = useSaleIdParam();
  const [saleNoteOpen, setSaleNoteOpen] = useState<boolean>(false);
  const { clearFilter } = useFilterStore(searchType);
  const { paginationModel, setGridSortModel, setPaginationModel } =
    useGridStore();
  const { hasReachedSearchLimit } = usePublicSearchLimit();
  const showSearchLimitNag =
    hasReachedSearchLimit && isRateLimitedPublicSearchType(searchType);
  useSharedSearchHydration();
  const { data: saleDetail, loading: loadingSaleDetail } = useGetSaleDetails({
    orgId,
    saleId,
  });
  const hasSaleNotes = Boolean(saleDetail?.saleNotes?.trim());
  const isSaleLotListing =
    searchType === SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS;

  const navigateToSaleCatalogues = () => {
    clearFilter();
    setGridSortModel([]);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    if (orgSegment) {
      router.push(
        buildSocietySearchHref(orgSegment, SEARCH_TYPE.SALE_CATALOGUES, null),
      );
    }
  };

  const getSaleName = () => {
    if (loadingSaleDetail) return 'Loading...';
    return saleDetail?.saleName || 'Sale not found';
  };

  useEffect(() => {
    if (searchType !== SEARCH_TYPE.MATING || !orgSegment) return;
    if (!isEbvEnabled) {
      router.replace(
        buildSocietySearchHref(orgSegment, SEARCH_TYPE.ANIMAL, null),
      );
    }
  }, [searchType, isEbvEnabled, orgSegment, router]);

  useEffect(() => {
    if (society.loading || isI4Society) return;
    if (
      searchType !== SEARCH_TYPE.DOWNLOADS &&
      searchType !== SEARCH_TYPE.ONLINE_REGISTRATIONS
    )
      return;
    if (!orgSegment) return;
    enqueueSnackbar(
      'Downloads and Online Registrations are only available for i4 registry societies.',
      { variant: SNACKBAR_VARIANT.INFO },
    );
    const nextParams = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : '',
    );
    nextParams.delete(SEARCH_PARAM.SEARCH_TYPE);
    nextParams.delete(SEARCH_PARAM.SALE_ID);
    router.replace(
      buildSocietySearchHref(orgSegment, SEARCH_TYPE.ANIMAL, nextParams),
    );
  }, [society.loading, isI4Society, searchType, orgSegment, router]);

  // Open results on navigation. SearchResult shows instructions instead
  // for Animals when no search criteria remain.
  useEffect(() => {
    setOpenResult(true);
  }, [searchType, setOpenResult]);

  return (
    <>
      {(searchType === SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS ||
        searchType === SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS ||
        searchType === SEARCH_TYPE.SALE_CATALOGUES_NOTES) && (
        <Stack
          direction="row"
          sx={{
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            columnGap: '12px',
            rowGap: '6px',
            minHeight: '36px',
            mt: isSaleLotListing ? '8px' : undefined,
            pt: 0.75,
            mb: 1.5,
          }}
        >
          <Button
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} aria-hidden />}
            sx={{
              width: { xs: 'auto', sm: 'min-content' },
              whiteSpace: 'nowrap',
              fontSize: '0.75rem',
              lineHeight: 1.2,
              py: 0.25,
              px: 1,
              minHeight: 30,
              '& .MuiButton-startIcon': { mr: 0.25, ml: -0.25 },
            }}
            variant="outlined"
            onClick={navigateToSaleCatalogues}
          >
            {isSaleLotListing ? 'Back to Sale Listing' : 'Back'}
          </Button>
          {isSaleLotListing && hasSaleNotes && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={() => setSaleNoteOpen(true)}
              sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
            >
              View Sale Notes
            </Button>
          )}
          {searchType === SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS && (
            <Typography sx={{ flex: 1 }}>{getSaleName()}</Typography>
          )}
          {searchType === SEARCH_TYPE.SALE_CATALOGUES_NOTES && (
            <Typography>Sale Notes</Typography>
          )}
        </Stack>
      )}
      {searchType === SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS && (
        <SaleInfoPanel loading={loadingSaleDetail} saleDetail={saleDetail} />
      )}
      {searchType === SEARCH_TYPE.MATING && isEbvEnabled && (
        <MatingPredictorPage />
      )}
      {searchType === SEARCH_TYPE.DOWNLOADS && isI4Society && <DownloadsPage />}
      {searchType === SEARCH_TYPE.ONLINE_REGISTRATIONS && isI4Society && (
        <OnlineRegistrationsTab />
      )}
      {searchType !== SEARCH_TYPE.SALE_CATALOGUES_NOTES &&
        searchType !== SEARCH_TYPE.MATING &&
        searchType !== SEARCH_TYPE.DOWNLOADS &&
        searchType !== SEARCH_TYPE.ONLINE_REGISTRATIONS &&
        !showSearchLimitNag && <SearchHeader />}
      {searchType === SEARCH_TYPE.SALE_CATALOGUES_NOTES && (
        <SaleNote saleId={Number(saleId)} />
      )}
      {searchType !== SEARCH_TYPE.SALE_CATALOGUES_NOTES &&
        searchType !== SEARCH_TYPE.MATING &&
        searchType !== SEARCH_TYPE.DOWNLOADS &&
        searchType !== SEARCH_TYPE.ONLINE_REGISTRATIONS &&
        showSearchLimitNag && <RateLimitBanner />}
      {searchType !== SEARCH_TYPE.SALE_CATALOGUES_NOTES &&
        searchType !== SEARCH_TYPE.MATING &&
        searchType !== SEARCH_TYPE.DOWNLOADS &&
        searchType !== SEARCH_TYPE.ONLINE_REGISTRATIONS &&
        !showSearchLimitNag && (
          // `key={searchType}` forces a remount of <SearchResult /> on
          // searchType change. Every public-search page.tsx re-exports this same
          // component, so without the key React keeps the instance and the prior
          // searchType's grid state (columns, DataGridPro apiRef, filter model,
          // scroll) lingers for a render — the "headers stay the same for a
          // second" effect. Remounting gives the new searchType a clean grid.
          <SearchResult key={searchType} />
        )}
      <Dialog
        open={saleNoteOpen}
        onClose={() => setSaleNoteOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{
          '& .MuiDialog-paper': {
            mx: { xs: 2, sm: 4 },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
          }}
        >
          Sale Notes
          <IconButton
            aria-label="close"
            onClick={() => setSaleNoteOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ px: { xs: 0, sm: 3 }, py: { xs: 0, sm: 2 } }}
        >
          {saleId != null && <SaleNote saleId={Number(saleId)} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
